import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import { editPlaces, getPlaceList } from "../store/slices/places";
import { nameregex, addressregex } from "../helper/regex";
import JoditEditor from "jodit-react";
import { GrUploadOption } from "react-icons/gr";
import { API_BASE_URL, API_PATHS } from "../utils/constants/api.constants";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css"; // important for styles

const EditPlace = () => {
  const editor = useRef(null);
  const { id } = useParams();
  const editorConfig = useMemo(
    () => ({
      buttons: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "align",
        "font",
        "fontsize",
        "paragraph",
        "|",
        "undo",
        "redo",
      ],
      placeholder: "Enter Description",
      removeButtons: ["image", "video", "file", "speech", "print", "preview"],
      readonly: false,
    }),
    []
  );
  let user = JSON.parse(localStorage.getItem("users"));
  const token = user.data.accessToken;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [visibility, setVisibility] = useState([]);
  const visibilityOptions = [
    { id: "Airport", name: "Airport" },
    { id: "Top Places", name: "Top Places" },
    { id: "Travel", name: "Travel" },
    { id: "Hourly Car", name: "Hourly Car" },
    { id: "Pickup Drop", name: "Pickup Drop" },
    { id: "Ferry", name: "Ferry" },
    { id: "Hotel", name: "Hotel" },
    { id: "Water Sports", name: "Water Sports" },
  ];
  const handleVisibilityChange = (selected) => {
    setVisibility(selected.map((item) => item.id));
  };

  // Fetch Place Details
  useEffect(() => {
    const fetchPlaceDetails = async () => {
      try {
        let item = { id };
        const data = await dispatch(getPlaceList(item)).unwrap();
        const place = data?.placesData[0];

        if (place) {
          setName(place.name || "");
          setDescription(place.description || "");
          setImages(place.image || []);
          setVisibility(place.visibility || []);
        }
      } catch (error) {
        console.error("Failed to fetch place details:", error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlaceDetails();
  }, [dispatch, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const validationErrors = { ...errors };

    switch (name) {
      case "name":
        setName(value);
        validationErrors[name] =
          value.trim() === "" || !nameregex.test(value)
            ? "Please enter a valid name"
            : "";
        break;

      case "description":
        setDescription(value);
        validationErrors[name] =
          value.trim() === "" || !addressregex.test(value)
            ? "Please enter a valid description"
            : "";
        break;

      default:
        break;
    }

    setErrors(validationErrors);
  };
  const imageUpload = async (e) => {
    setImageError("");
    const files = Array.from(e.target.files);

    if (files.length === 0) {
      setImageError("Please select at least one file to upload");
      return;
    }
    if (images.length + files.length > 3) {
      setImageError("You can upload a maximum 3 images");
      return;
    }

    const newImage = [];
    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setImageError("Please upload image less than 5 MB");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      const requestOptions = {
        method: "POST",
        body: formData,
        headers: {
          "x-access-token": token,
        },
      };

      try {
        const response = await fetch(
          `${API_BASE_URL}${API_PATHS.uploadFile}`,
          requestOptions
        );

        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.status === 1 && data?.data?.location) {
          newImage.push({
            name: file.name,
            mediaLink: data.data.location,
            isActive: true,
          });
        } else {
          throw new Error(data.message || "Failed to upload image");
        }
      } catch (error) {
        setImageError(
          error.message || "Something went wrong while uploading the image"
        );
        return;
      }
    }

    setImages((prevImage) => [...prevImage, ...newImage]);
    e.target.value = null;
  };

  const removeImage = (index) => {
    setImages((prevImage) => {
      const updatedImage = [...prevImage];
      updatedImage.splice(index, 1);

      if (updatedImage.length === 0) {
        setImageError("Please upload at least one image");
      } else {
        setImageError("");
      }

      return updatedImage;
    });
  };
  const EditPlaces = async (e) => {
    e.preventDefault();
    setIsDisabled(true);
    setTimeout(() => setIsDisabled(false), 3000);

    const stripHtmlTags = (html) => {
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    };

    const validationErrors = {};
    if (!name.trim()) {
      validationErrors.name = "Please enter name";
    } else if (!nameregex.test(name)) {
      validationErrors.name = "Name must be at least three characters";
    }

    if (visibility.length === 0) {
      validationErrors.visibility =
        "Please select at least one visibility option";
    }

    if (!description.trim() || stripHtmlTags(description).length === 0) {
      validationErrors.description = "Please enter a valid description";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const item = {
      id,
      name,
      description,
      image: images.map((image) => ({
        name: image.name,
        mediaLink: image.mediaLink,
        isActive: true,
      })),
      visibility,
    };

    try {
      await dispatch(editPlaces(item)).unwrap();
      navigate("/admin/places");
      alert("Place details edited successfully!");
    } catch (error) {
      alert(error);
    }
  };

  // Handle Cancel
  const cancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <h6 className="fw-semibold mb-0">Edit Place</h6>
        <ul className="d-flex align-items-center gap-2">
          <li className="fw-medium">
            <Link
              to="/admin/places"
              className="d-flex align-items-center gap-1 hover-text-primary"
            >
              Place
            </Link>
          </li>
          <li> &gt; </li>
          <li className="fw-medium">Edit Place</li>
        </ul>
      </div>
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={EditPlaces}>
            <div className="row mb-40">
              <div className="col-md-5">
                <label className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  placeholder="Enter Place Name"
                />
                {errors.name && (
                  <div className="text-danger mt-2">{errors.name}</div>
                )}
              </div>

              <div className="col-md-5">
                <label className="form-label">
                  Images <span className="text-danger">*</span>
                </label>
                <br />
                <label htmlFor="fileInput" className="custom-button">
                  <GrUploadOption
                    style={{ fontSize: "16px", lineHeight: "18px" }}
                  />
                  &nbsp; Upload Image
                </label>
                <input
                  type="file"
                  id="fileInput"
                  name="image"
                  onChange={imageUpload}
                  className={`form-control ${imageError ? "is-invalid" : ""}`}
                  multiple
                />
                {imageError && (
                  <div className="text-danger mt-2">{imageError}</div>
                )}
                <br />
                <label className="form-label mt-3">Current Images</label>
                <div className="row">
                  {images.map((img, index) => (
                    <div
                      className="col-md-4 mb-2 position-relative"
                      key={index}
                    >
                      <img
                        src={img.mediaLink}
                        style={{ height: "120px", objectFit: "cover" }}
                        alt=""
                        className="img-thumbnail"
                      />
                      <button
                        className="image-remove-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          removeImage(index);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-md-10 mt-3">
                <label className="form-label">
                  Visibility <span className="text-danger">*</span>
                </label>
                <Typeahead
                  id="visibility-typeahead"
                  labelKey="name"
                  multiple
                  className="input1"
                  onChange={handleVisibilityChange}
                  options={visibilityOptions}
                  placeholder="Select Visibility"
                  selected={visibilityOptions.filter((option) =>
                    visibility.includes(option.id)
                  )}
                />
                {errors.visibility && (
                  <div className="text-danger mt-2">{errors.visibility}</div>
                )}
              </div>

              <div className="col-md-10 mt-3">
                <label className="form-label">
                  Description <span className="text-danger">*</span>
                </label>
                <JoditEditor
                  ref={editor}
                  name="description"
                  value={description}
                  onChange={(content) => setDescription(content)}
                  config={editorConfig}
                />
                {errors.description && (
                  <div className="text-danger mt-2">{errors.description}</div>
                )}
              </div>
            </div>
            {/* Buttons */}
            <div className="d-flex align-items-center justify-content-center gap-3">
              <button
                type="button"
                onClick={cancel}
                className="btn btn-outline-primary-600 wd-100 radius-8 px-14 py-6 text-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isDisabled}
                className="btn btn-primary-600 wd-100 radius-8 px-14 py-6 text-md"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditPlace;
